import React, { FC, PropsWithChildren, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

import {
    subtitlesPosition,
    SubtitlesPosition,
} from '@/scenarios/legacy-player/components/PlayerSkin/parts/Subtitles/Subtitles.types';
import { Subtitles as SubtitlesView } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Subtitles';
import { getSubtitlesDirection } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Subtitles/helpers';
import { useSubtitles } from '@/scenarios/legacy-player/hooks/useSubtitles';
import { useCurrentTextTrack } from '@/scenarios/legacy-player/hooks/useCurrentTextTrack';
import { useRuntimeConfig } from '@/shared/hooks/useRuntimeConfig';

import {
    SubtitleSettingsSettersContext,
    SubtitleSettingsContext,
} from '../../../contexts/subtitles';
import { SubtitleSettings } from '../../../types/subtitles';
import { isSubtitlesSelectEnabled } from '../../../utils/is-subtitles-select-enabled';

import { useRatio } from './hooks/use-ratio';

export const SubtitlesProvider: FC<PropsWithChildren> = ({ children }) => {
    const subtitles = useSubtitles();
    const currentTextTrack = useCurrentTextTrack();
    const [ratio, setRatio] = useRatio();
    const [position, setPosition] = useState<SubtitlesPosition>(subtitlesPosition.BOTTOM);
    const [subtitlesContainer, setSubtitlesContainer] = useState<HTMLElement | null>(null);
    const hasTextTrack = !!currentTextTrack;
    const runtimeConfig = useRuntimeConfig();
    const subtitleSettingsSetters = useMemo(
        () => ({
            setSubtitlesPosition: setPosition,
            setSubtitlesRatio: setRatio,
            setSubtitlesContainer,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const subtitleSettings = useMemo<SubtitleSettings>(
        () => ({
            ratio,
            position,
            isActive: hasTextTrack,
        }),
        [position, ratio, hasTextTrack]
    );

    return (
        <SubtitleSettingsSettersContext.Provider value={subtitleSettingsSetters}>
            <SubtitleSettingsContext.Provider value={subtitleSettings}>
                {children}
                {subtitlesContainer &&
                    hasTextTrack &&
                    !!subtitles?.length &&
                    ReactDOM.createPortal(
                        <SubtitlesView
                            subtitles={subtitles}
                            subtitlesRatio={ratio}
                            subtitlesDirection={getSubtitlesDirection(currentTextTrack.lang)}
                            isSelectEnabled={isSubtitlesSelectEnabled({
                                uatraits: runtimeConfig.get('uatraits'),
                            })}
                        />,
                        subtitlesContainer
                    )}
            </SubtitleSettingsContext.Provider>
        </SubtitleSettingsSettersContext.Provider>
    );
};
